
export enum UnitOfMeasure {
  KILOMETER = "km",
  MILE = "M",
  METER = "m"
}

export type Distance = {
  units: number | undefined;
  unitOfMeasure: UnitOfMeasure | undefined;
};

export type Pace = {
  time: Time;
  distance: Distance;
};

export type Time = {
  hours: number;
  minutes: number;
  seconds: number;
};

enum SignificantDistance {
  HalfMarathon = 21097.5,
  Marathon = 42195
}

const SecondsPerMinute = 60;
const SecondsPerHour = 60 * SecondsPerMinute;

const MetersPerKilometer = 1000;
const MetersPerMile = 1609.34;

const UnitOfMeasureMeterDistance = {
  [UnitOfMeasure.KILOMETER]: MetersPerKilometer,
  [UnitOfMeasure.MILE]: MetersPerMile,
  [UnitOfMeasure.METER]: 1
};

export type PaceOptions = {
  pacePerGivenDistance: Distance;
};

/**
 *  takes a distance and pace, and return the total elapsed time
 * @param distance
 * @param pace
 */
export const calculateTime = (distance: Distance, pace: Pace): Time => {
  const paceTimeInSeconds = timeToSeconds(pace.time);
  const paceDistanceInMeters = distanceToMeters(pace.distance);
  const distanceInMeters = distanceToMeters(distance);
  const elapsedTimeInSeconds =
    distanceInMeters * (paceTimeInSeconds / paceDistanceInMeters);
  return secondsToTime(elapsedTimeInSeconds);
};

/**
 *  takes a distance and a time and returns the pace in the optional desired unit-of-measure
 *  If not unit-of-measure is specified, it returns in the unit of the provided distance
 * @param time
 * @param distance
 * @param options
 */
export const calculatePace = (
  time: Time,
  distance: Distance,
  options: PaceOptions
): Pace => {
  const elapsedTimeInSeconds = timeToSeconds(time);
  const distanceInMeters = distanceToMeters(distance);
  const secondsPerMeter = elapsedTimeInSeconds / distanceInMeters;
  const pacePerDistanceInMeter = distanceToMeters(options.pacePerGivenDistance);
  return {
    time: secondsToTime(secondsPerMeter * pacePerDistanceInMeter),
    distance: options.pacePerGivenDistance
  };
};

/**
 *  takes a time and a pace and returns the distance in the optional desired unit-of-measure
 *  If not unit-of-measure is specified, it returns in the unit-of-measure the provided Pace
 * @param time
 * @param pace
 * @param options
 */
export type DistanceOptions = {
  unitOfMeasure: UnitOfMeasure;
};

export const calculateDistance = (
  time: Time,
  pace: Pace,
  options?: DistanceOptions
): Distance => {
  const elapsedTimeInSeconds = timeToSeconds(time);
  const paceTimeInSeconds = timeToSeconds(pace.time);
  const metersPerUnitOfMeasure =
    UnitOfMeasureMeterDistance[pace.distance.unitOfMeasure!];
  const paceDistanceInMeters = distanceToMeters(pace.distance);
  const distanceInMeters =
    elapsedTimeInSeconds * (paceDistanceInMeters / paceTimeInSeconds);
  const unitsInDesiredUnitOfMeasure =
    options && options.unitOfMeasure
      ? distanceInMeters / UnitOfMeasureMeterDistance[options.unitOfMeasure]
      : distanceInMeters / metersPerUnitOfMeasure;

  return metersToDistance(
    distanceInMeters,
    (options && options.unitOfMeasure) || pace.distance.unitOfMeasure!
  );
};

export const paceEquivalents = (pace: Pace, distances: Distance[]): Pace[] =>
  distances.map(distance => ({
    time: calculateTime(distance, pace),
    distance
  }));

const distanceToMeters = (distance: Distance): number => {
  const metersPerUnitOfMeasure =
    UnitOfMeasureMeterDistance[distance.unitOfMeasure!];
  return distance.units! * metersPerUnitOfMeasure;
};

const metersToDistance = (
  meters: number,
  unitOfMeasure: UnitOfMeasure
): Distance => ({
  units: roundToSignificantDigits(
    meters / UnitOfMeasureMeterDistance[unitOfMeasure]
  ),
  unitOfMeasure
});

export const timeToSeconds = (time: Time): number => {
  let seconds = time.seconds;
  seconds += time.minutes ? time.minutes * SecondsPerMinute : 0;
  seconds += time.hours ? time.hours * SecondsPerHour : 0;
  return seconds;
};

const roundToSignificantDigits = (value: number) =>
  Math.round((value + Number.EPSILON) * 100) / 100;

export const secondsToTime = (seconds: number): Time => ({
  hours: Math.floor(seconds / SecondsPerHour),
  minutes: Math.floor((seconds % SecondsPerHour) / SecondsPerMinute),
  seconds: roundToSignificantDigits(
    (seconds % SecondsPerHour) % SecondsPerMinute
  )
});

const renderTimeComponent = (timeComponent: number) =>
  timeComponent
    ? timeComponent < 10
      ? `0${timeComponent}`
      : timeComponent
    : "00";

const renderHourTimeComponent = (timeComponent: number) =>
  timeComponent ? `${timeComponent}` : "";

export const formatTime = (time: Time) =>
  `${
    time.hours ? `${renderHourTimeComponent(time.hours)}:` : ""
  }${renderTimeComponent(time.minutes)}:${renderTimeComponent(time.seconds)}`;

export const parseTime = (time: string): Time | undefined => {
  let parsedTime: Time = { hours: 0, minutes: 0, seconds: 0 };
  const timeComponents = time.split(":").reverse();
  for (let index = 0; index < timeComponents.length; index++) {
    const value = timeComponents[index];
    const timeComponent = (value.trim() !== "") ? Number(value) : undefined; // Number("") is 0... but thats not desired
    if (timeComponent !== undefined && !isNaN(timeComponent)) {
      if (index === 0) {
        parsedTime = { ...parsedTime, seconds: timeComponent };
      } else if (index === 1) {
        parsedTime = { ...parsedTime, minutes: timeComponent };
      } else if (index === 2) {
        parsedTime = { ...parsedTime, hours: timeComponent };
      }
    } else {
      // Time is invalid if one of the values separated by : is not a number
      return undefined;
    }
  }
  return parsedTime;
};

// undefined is no time
export const isNoTime = (time: Time | undefined) => time ? timeToSeconds(time) === 0 : true;

export const parseDistance = (
  distance: string | undefined,
  unitOfMeasure: string | undefined
): Distance | undefined => {
  const parsedDistanceValue = Number(distance);
  const units = !isNaN(parsedDistanceValue) ? parsedDistanceValue : undefined;
  const parsedUnitOfMeasure = parseUnitOfMeasure(unitOfMeasure);

  return { units, unitOfMeasure: parsedUnitOfMeasure };
};

export const formatDistance = (distance: Distance) =>
  `${distance.units} ${distance.unitOfMeasure}`;

export type DistanceParams = {
  units: string;
  unitOfMeasure: string;
};

export const parsePace = (
  time: string,
  distance: DistanceParams
): Pace | undefined => {
  const parsedTime = parseTime(time);
  const parsedDistance = parseDistance(distance.units, distance.unitOfMeasure);
  return parsedTime && parsedDistance
    ? { time: parsedTime, distance: parsedDistance }
    : undefined;
};

export const formatPace = (pace: Pace) =>
  `${formatTime(pace.time)} / ${formatDistance(pace.distance)}`;

const parseUnitOfMeasure = (
  unit: string | undefined
): UnitOfMeasure | undefined => {
  return (Object.values(UnitOfMeasure) as string[]).includes(unit!)
    ? (unit as UnitOfMeasure)
    : undefined;
};

export const getPaceTable = (
  pace: Pace,
  maxDistance: Distance,
  minDistance?: Distance
) => {
  const defaultMinDistance: Distance = {
    units: 0,
    unitOfMeasure: maxDistance.unitOfMeasure
  };
  minDistance = minDistance || defaultMinDistance;
  const paceTableIncrements = generatePaceTableIncrements(
    pace,
    minDistance,
    maxDistance
  );
  const distances: Distance[] = paceTableIncrements.map(increment => ({
    units: increment,
    unitOfMeasure: pace.distance.unitOfMeasure
  }));
  return paceEquivalents(pace, distances);
};

const generatePaceTableIncrements = (
  pace: Pace,
  minDistance: Distance,
  maxDistance: Distance
): number[] => {
  const unitOfMeasure = pace.distance.unitOfMeasure;
  const interval: number[] = [];
  const roundUpToNearest1000 = (value: number) =>
    Math.ceil(value / 1000) * 1000;
  const roundDownToNearest1000 = (value: number) =>
    Math.floor(value / 1000) * 1000;

  const minDistanceInPaceUnits = metersToDistance(
    distanceToMeters(minDistance),
    pace.distance.unitOfMeasure!
  );
  const maxDistanceInPaceUnits = metersToDistance(
    distanceToMeters(maxDistance),
    pace.distance.unitOfMeasure!
  );
  const rangeSize =
    maxDistanceInPaceUnits.units! - minDistanceInPaceUnits.units!;

  if (unitOfMeasure === UnitOfMeasure.METER) {
    const increment = rangeSize <= 800 ? (rangeSize <= 400 ? 100 : 200) : 400;

    let current =
      minDistanceInPaceUnits.units === 0
        ? increment
        : minDistanceInPaceUnits.units!;
    while (current < maxDistanceInPaceUnits.units!) {
      const nextCurrent = current + increment;
      const currentNearest1000 = roundUpToNearest1000(current);
      const nextCurrentNearest1000 = roundDownToNearest1000(nextCurrent);
      interval.push(current);
      if (
        currentNearest1000 === nextCurrentNearest1000 &&
        current !== currentNearest1000 &&
        nextCurrent !== nextCurrentNearest1000
      ) {
        // we have crossed over a factor of 1000
        interval.push(currentNearest1000);
      }
      current = nextCurrent;
    }
  } else if (
    unitOfMeasure === UnitOfMeasure.KILOMETER ||
    unitOfMeasure === UnitOfMeasure.MILE
  ) {
    const increment = rangeSize <= 2 ? (rangeSize <= 1 ? 0.25 : 0.5) : 1;
    let current =
      minDistanceInPaceUnits.units === 0
        ? increment
        : minDistanceInPaceUnits.units!;
    while (current < maxDistanceInPaceUnits.units!) {
      const nextCurrent = current + increment;
      const significantDistances = Object.values(SignificantDistance);
      interval.push(current);
      for (const distance of Object.values(significantDistances)) {
        if (
          current < distance &&
          distance < nextCurrent &&
          current !== distance &&
          nextCurrent !== distance
        ) {
          interval.push(
            metersToDistance(Number(distance), pace.distance.unitOfMeasure!)
              ?.units!
          );
        }
      }
      current = nextCurrent;
    }
  }
  (maxDistanceInPaceUnits.units !== interval[interval.length - 1]) && interval.push(maxDistanceInPaceUnits.units!);
  return interval;
};

export const isValidDistance = (distance: Distance | undefined) =>
  !!distance &&
  !!distance.units &&
  !isNaN(distance.units) &&
  !!distance.unitOfMeasure;

// TODO look at what we do when the TimeControl passes back an F'd time. We need to do the same thing there
export const isValidTime = (time: Time | undefined) => !!time;
